import { fileName, saveBlob } from './exportTable.js';

/**
 * บันทึกกรอบแคนวาสเป็นไฟล์ PNG
 *
 * ────────────────────────────────────────────────────────────────
 *  ฟังก์ชันนี้จับ "กรอบ" ไม่ได้จับ "ผัง" — ผู้เรียกต้องจัดผังให้พอดีกรอบก่อน
 * ────────────────────────────────────────────────────────────────
 *
 * เคยลองอีกทางมาแล้วและไม่ผ่าน คือจับกิ่ง .react-flow__viewport โดยตรง
 * แล้วบอกขนาดกับ transform เองเพื่อให้ได้ผังเต็มโดยไม่สนขนาดจอ
 * ผลคือรูปยังถูกตัดด้านข้างอยู่ดี — ขนาดที่คำนวณจาก getNodesBounds
 * ไม่ตรงกับสิ่งที่ html-to-image วาดจริง
 *
 * ทางที่ใช้อยู่ตอนนี้จึงตรงไปตรงมากว่า: ให้ผู้เรียกสั่ง fitView ก่อน
 * ผังจึงอยู่ในกรอบครบทุกกิ่งอยู่แล้วตั้งแต่ต้น เราแค่ถ่ายกรอบนั้นออกมา
 *
 * ⚠ ถ้าเรียกโดยไม่ fitView ก่อน จะได้เฉพาะส่วนที่มองเห็น
 *   ซึ่งเป็นบั๊กที่ผู้ใช้เจอมาแล้วสองรอบ (หัวผังหายบ้าง ด้านข้างขาดบ้าง)
 *
 * ────────────────────────────────────────────────────────────────
 *  ข้อจำกัดของเครื่องมือที่ใช้
 * ────────────────────────────────────────────────────────────────
 *
 * html-to-image วาดรูปด้วยการโคลน DOM ลง <foreignObject> ของ SVG
 * ซึ่งอ่านทรัพยากรจากภายนอกไม่ได้เลย แม้แต่ไฟล์ใน origin เดียวกัน
 *
 * ⚠ อะไรที่หน้าจออ้างเป็น url(...) ไปยังไฟล์ จะหายไปจากรูปเงียบ ๆ
 *   ลายอัตลักษณ์บนพื้นแคนวาสจึงต้องฝังเป็น data URI ใน styles.css (ดูหมายเหตุที่นั่น)
 *   ถ้าวันหนึ่งมีคนเปลี่ยนกลับไปเป็น url('/brand/...') ลายจะหายจากรูป
 *   โดยที่หน้าจอยังดูปกติทุกอย่าง
 *
 * ⚠ ฟอนต์ก็เรื่องเดียวกัน แต่ html-to-image ดึง @font-face มาฝังให้เอง
 *   จึงต้องเป็นฟอนต์ที่เสิร์ฟจาก origin เดียวกัน (ซึ่งตอนนี้เป็นอย่างนั้นแล้ว)
 *   ถ้าย้ายฟอนต์กลับไป Google Fonts ตัวหนังสือในรูปจะกลายเป็นฟอนต์ระบบ
 */

/**
 * เพดานของด้านที่ยาวที่สุด และของจำนวนพิกเซลทั้งรูป
 *
 * เบราว์เซอร์มีเพดานขนาด canvas อยู่ (ราว 16,000 พิกเซลต่อด้าน และจำกัดพื้นที่รวมด้วย)
 * เกินแล้วไม่ได้ error อะไรเลย แต่ได้รูปเปล่ากลับมา ซึ่งเป็นความล้มเหลวแบบที่แย่ที่สุด —
 * ผู้ใช้ได้ไฟล์ ดูเหมือนสำเร็จ แต่เปิดมาไม่มีอะไรอยู่ข้างใน
 *
 * ตั้งไว้ต่ำกว่าเพดานจริงพอสมควร แล้วยอมลดความละเอียดลงแทนการปล่อยให้พัง
 * 40 ล้านพิกเซลคือราว 8,000 × 5,000 ซึ่งกินหน่วยความจำตอนวาดประมาณ 160 MB
 */
const MAX_SIDE = 14000;
const MAX_AREA = 40_000_000;

export interface PngOptions {
  /**
   * ระดับซูมของผังตอนที่กำลังจะถ่าย (1 = ขนาดจริงของการ์ด)
   *
   * ⚠ ตัวนี้คือสิ่งที่ทำให้ตัวหนังสือในรูปคมหรือไม่คม
   *
   * fitView ย่อผังลงให้พอดีกรอบ ผังทั้งกระทรวงอาจเหลือ 0.3 เท่า
   * ถ้าถ่ายที่ 2 เท่าของขนาดจอเฉย ๆ ตัวหนังสือจะถูกวาดที่ 0.3 × 2 = 0.6 เท่า
   * ของขนาดจริง แล้วถูกขยายกลับตอนเปิดดู — เบลอตั้งแต่ต้นทาง ไม่ใช่ตอนเปิด
   *
   * ส่งระดับซูมมาให้ แล้วเราชดเชยกลับด้วยการเพิ่มความละเอียดในสัดส่วนเดียวกัน
   */
  zoom?: number;
  /** สีพื้นของรูป — ไม่ส่งมา = อ่านจากกล่อง .flowbox ที่ครอบอยู่ (ผังหน่วยงาน) */
  background?: string;
}

export async function exportPng(
  frame: HTMLElement,
  title: string,
  opts: PngOptions = {},
): Promise<void> {
  /*
   * โหลดตอนกด ไม่ใช่ตอนเปิดหน้า — เหตุผลเดียวกับ pdfmake/exceljs ใน exportTable.ts
   * คนส่วนใหญ่เปิดผังดูแล้วปิดไปโดยไม่เคยกดปุ่มนี้
   */
  const { toBlob } = await import('html-to-image');

  /*
   * สีพื้นต้องอ่านจากของจริง ไม่ใช่เขียนค่าสีไว้ตรงนี้
   * แคนวาสเองพื้นใส สีมาจากกล่องที่ครอบอยู่ และเปลี่ยนตามโหมดสว่าง/มืด
   * ถ้าไม่ส่งสีไป พื้นจะโปร่งใส แล้วรูปที่เอาไปแปะในเอกสารพื้นขาวจะดูปกติ
   * แต่พอแปะในสไลด์พื้นเข้ม ตัวหนังสือสีเข้มจะอ่านไม่ออกทั้งรูป
   */
  const host = frame.closest<HTMLElement>('.flowbox') ?? frame;
  const bg = opts.background ?? getComputedStyle(host).backgroundColor;

  const w = frame.clientWidth;
  const h = frame.clientHeight;
  if (!(w > 0 && h > 0)) throw new Error('แคนวาสยังไม่มีขนาด');

  /*
   * ความละเอียดที่อยากได้ = 2 เท่าของ "ขนาดจริงของการ์ด" ไม่ใช่ 2 เท่าของขนาดจอ
   * ผังที่ถูกย่อลงครึ่งหนึ่งจึงต้องถ่ายที่ 4 เท่าเพื่อให้ได้ความคมเท่ากัน
   */
  const zoom = opts.zoom && opts.zoom > 0 ? opts.zoom : 1;
  const wanted = 2 / zoom;

  /*
   * แล้วค่อยรูดลงมาให้อยู่ในเพดาน
   * ⚠ ต้องดูทั้งความยาวด้านและพื้นที่รวม อย่างใดอย่างหนึ่งไม่พอ
   *   ผังแนวนอนยาว ๆ ชนเพดานด้านก่อน ส่วนผังจัตุรัสใหญ่ ๆ ชนเพดานพื้นที่ก่อน
   */
  /*
   * ⚠ พื้นล่างไม่ใช่ 1 อีกแล้ว — ผังผู้บริหารกางทั้งหมดกว้างกว่า 20,000px ที่ขนาดจริง
   *   ถ้าบังคับ ≥ 1 canvas จะเกินเพดานของเบราว์เซอร์แล้วรูปขาดด้านขวาเงียบ ๆ (เจอ 19 ก.ย. 2569)
   *   ผังหน่วยงานไม่กระทบ — กรอบของมันเท่าจอเสมอ เพดานจึงไม่เคยต่ำกว่า 1 อยู่แล้ว
   */
  const ratio = Math.max(
    0.2,
    Math.min(wanted, MAX_SIDE / Math.max(w, h), Math.sqrt(MAX_AREA / (w * h))),
  );

  const blob = await toBlob(frame, {
    pixelRatio: ratio,
    backgroundColor: bg,
    filter: (node) => {
      if (!(node instanceof HTMLElement)) return true;
      // ของเราเอง เช่น ปุ่มซูมมุมล่างขวา
      if (node.dataset.noexport === 'true') return false;
      /*
       * ป้าย "React Flow" มุมล่างขวา
       *
       * ตัดเฉพาะตอนส่งออก ไม่ได้ซ่อนบนหน้าจอ — หน้าจอยังตั้ง hideAttribution: false ไว้
       * (ดู OrgFlow.tsx) ซึ่งเป็นการตัดสินใจของคนที่เขียนไว้ก่อน ห้ามไปเปลี่ยนจากที่นี่
       *
       * เหตุผลที่ตัดจากรูป: รูปที่ได้ถูกเอาไปแนบเรื่องและแปะสไลด์ในนามหน่วยงาน
       * ป้ายชื่อไลบรารีที่ใช้วาดไม่ได้เป็นส่วนหนึ่งของเนื้อหาที่กำลังสื่อ
       *
       * ⚠ ต้องเทียบด้วย classList ไม่ใช่ data-attribute
       *   โหนดนี้ React Flow สร้างเอง เราแทรก data-noexport เข้าไปไม่ได้
       *   ถ้าอัปเกรดแล้วป้ายกลับมาโผล่ในรูป ให้มาดูว่าชื่อคลาสเปลี่ยนไปหรือยัง
       */
      if (node.classList.contains('react-flow__attribution')) return false;
      return true;
    },
  });

  if (!blob) throw new Error('สร้างรูปไม่สำเร็จ');
  saveBlob(blob, fileName(title, 'png'));
}
